import React from 'react'
import {
  X, ExternalLink, Plus, Search, Sparkles, Loader2,
} from 'lucide-react'
import { RESOURCE_TYPE_COLORS } from '../../constants/colors'
import { RESOURCE_TYPES } from '../../constants/resourceTypes'
import type { ConnectionsNode, ConnectionsEdge } from '../../types'

interface NodeDetailPanelProps {
  node: ConnectionsNode | null
  edges: ConnectionsEdge[]
  allNodes: ConnectionsNode[]
  onClose: () => void
  onOpenResource?: (node: ConnectionsNode) => void
  onAddToList?: (node: ConnectionsNode) => void
  onFindRelated?: (node: ConnectionsNode) => void
  onDiscoverMore?: (node: ConnectionsNode) => void
  isDiscovering?: boolean
}

const NodeDetailPanel: React.FC<NodeDetailPanelProps> = ({
  node,
  edges,
  allNodes,
  onClose,
  onOpenResource,
  onAddToList,
  onFindRelated,
  onDiscoverMore,
  isDiscovering,
}) => {
  if (!node) return null

  const color = RESOURCE_TYPE_COLORS[node.type] || '#6B7280'
  const typeLabel = RESOURCE_TYPES.find(r => r.id === node.type)?.label || node.type

  // Count connections for this node
  const connectionCount = edges.filter(
    e => e.source === node.id || e.target === node.id
  ).length

  // Check if any connection is AI-discovered
  const hasAiDiscovered = edges.some(
    e => (e.source === node.id || e.target === node.id) && e.ai_discovered
  )

  // Find connected node labels for preview
  const connectedNodes = edges
    .filter(e => e.source === node.id || e.target === node.id)
    .map(e => {
      const otherId = e.source === node.id ? e.target : e.source
      return allNodes.find(n => n.id === otherId)
    })
    .filter(Boolean)
    .slice(0, 5)

  return (
    <div className="absolute top-0 right-0 w-80 h-full bg-white border-l border-gray-200
                    shadow-lg z-20 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Resource Details</h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-100 transition-colors"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Type badge */}
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
            style={{ backgroundColor: color }}
          >
            {typeLabel}
          </span>
          {hasAiDiscovered && (
            <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider
                             px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
              <Sparkles className="w-3 h-3" />
              AI-Discovered
            </span>
          )}
        </div>

        {/* Title */}
        <h4 className="text-sm font-semibold text-gray-900 leading-snug">
          {node.label}
        </h4>

        {/* Score indicator */}
        {node.score != null && node.score > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Relevance Score</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(node.score * 100, 100)}%`,
                    backgroundColor: color,
                  }}
                />
              </div>
              <span className="text-xs font-semibold text-gray-700">
                {(node.score * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        )}

        {/* Connection count */}
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">Connections</p>
          <p className="text-sm text-gray-700">
            {connectionCount} connection{connectionCount !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Connected resources preview */}
        {connectedNodes.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1.5">Connected To</p>
            <div className="space-y-1">
              {connectedNodes.map((cn) => cn && (
                <div
                  key={cn.id}
                  className="flex items-center gap-2 text-xs text-gray-500 py-1 px-2
                             bg-gray-50 rounded"
                >
                  <div
                    className="w-2 h-2 rounded-sm shrink-0"
                    style={{ backgroundColor: RESOURCE_TYPE_COLORS[cn.type] || '#6B7280' }}
                  />
                  <span className="truncate">{cn.label}</span>
                </div>
              ))}
              {connectionCount > 5 && (
                <p className="text-[10px] text-gray-400 px-2">
                  +{connectionCount - 5} more
                </p>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="pt-3 border-t border-gray-100 space-y-2">
          {onOpenResource && (
            <button
              onClick={() => onOpenResource(node)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium
                         text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open Resource
            </button>
          )}

          {onAddToList && (
            <button
              onClick={() => onAddToList(node)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium
                         text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add to List
            </button>
          )}

          {onFindRelated && (
            <button
              onClick={() => onFindRelated(node)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium
                         text-acad-700 bg-acad-50 hover:bg-acad-100 rounded-lg transition-colors"
            >
              <Search className="w-3.5 h-3.5" />
              Find Related
            </button>
          )}

          {onDiscoverMore && (
            <button
              onClick={() => onDiscoverMore(node)}
              disabled={isDiscovering}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium
                         text-white bg-acad-500 hover:bg-acad-600 rounded-lg transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDiscovering ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {isDiscovering ? 'Discovering...' : 'Discover 25 More from Here'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default NodeDetailPanel
